import { useDispatch, useSelector } from 'react-redux';
import { RootState, setAppLoading } from 'store';
import { useEffect, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';

import * as S from '../styles';
import { toast } from 'react-toastify';
import { PaymentClient } from 'v3/api/client/PaymentClient';
import Field from 'v3/components/Field';
import Button from 'v3/components/Button';
import { SIGN_FORM_ID, renderCtaInSlot, useCtaSlot } from './cta-slot';
import { useUnsupportedCardBrand } from 'v3/hooks/useUnsupportedCardBrand';
import { UNSUPPORTED_CARD_BRAND_MESSAGE } from 'v3/constants/cardBrands';


interface CustomerAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

interface CustomerData {
  firstName: string;
  lastName: string;
  email: string;
  userPhone: string;
  address?: CustomerAddress;
}

type CreditCardFormProps = {
  validateOnSubmit: () => boolean;
  signContract: (paymentParams: { cardIdentifier?: string; plaidAccountId?: string; plaidPublicToken?: string }) => void;
  isDrawingValid?: boolean;
  clientSecret: string;
  customerData: CustomerData;
};

export const CCForm = ({
  isDrawingValid,
  validateOnSubmit,
  signContract,
  clientSecret,
  customerData,
}: CreditCardFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [isCardValid, setIsCardValid] = useState(false);
  const ctaSlot = useCtaSlot();

  const stripe = useStripe();
  const elements = useElements();
  const { unsupportedBrand, checkBrand } = useUnsupportedCardBrand();

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    // An unsupported brand can never be set up, so it counts as an invalid
    // card here and the CTA below stays disabled on the same flag.
    const isUnsupported = checkBrand(event.brand);
    setIsCardValid(event.complete && !isUnsupported);

    if (event.error) {
      console.log(event.error.message);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if(!validateOnSubmit()) {
      return;
    }

    if (!stripe || !elements) return;
    // Empty when setting up the payment intent failed; Stripe throws on an empty secret.
    if (!clientSecret) {
      toast.error("We couldn't start the card check. Please refresh and try again.");
      return;
    }
    // The CTA is already disabled for these, but a submit can still arrive from
    // a stale render or the Enter key, and the decline is guaranteed.
    if (unsupportedBrand) {
      toast.error(UNSUPPORTED_CARD_BRAND_MESSAGE);
      return;
    }
    setIsLoading(true);
    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setIsLoading(false);
      return;
    }

    setLoadingStatus('Checking credit card');

    const billingDetails = {
      name: `${customerData.firstName} ${customerData.lastName}`,
      email: customerData.email,
      phone: customerData.userPhone,
      tax_id: null as string | null,
      address: {
        line1: customerData?.address?.line1,
        line2: customerData?.address?.line2,
        city: customerData?.address?.city,
        state: customerData?.address?.state?.replace('BR-', '').replace('US-', '').replace('PT-', ''),
        country: customerData?.address?.country,
        postal_code: customerData?.address?.postalCode,
      }
    };

    // 2. Confirm Card Setup to verify the card details
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: billingDetails,
      },
    });

    if (error) {
      console.error("Validation failed:", error.message);
      toast.error(error.message || 'Error validating credit card');
      setIsLoading(false);
      // dispatch(setAppLoading(false));
      // showOverlay(false);
      setLoadingStatus('');
      return;
    }

      // 3. Setup successful! setupIntent.payment_method is your new 'ID'
    // console.log("Verified Payment Method ID:", setupIntent.payment_method);

    try {
      setLoadingStatus('Generating membership');
      await signContract({cardIdentifier: setupIntent.payment_method as string});
      setLoadingStatus('Finishing');
    } catch (error) {
      setIsLoading(false);
      // dispatch(setAppLoading(false));
      // showOverlay(false);
    }

  };

  return (
    <>
      <form onSubmit={handleSubmit} id={SIGN_FORM_ID}>
        <S.CardElementWrapper>
          <CardElement
            onChange={handleCardChange}
            /* Stripe renders these inputs inside its own iframe, so page CSS cannot
               reach them — the dark treatment has to come through this option. */
            options={{
              style: {
                base: {
                  fontSize: '20px',
                  color: '#ffffff',
                  lineHeight: '45px',
                  backgroundColor: '#2b2a3a',
                  iconColor: '#ffffff',

                  '::placeholder': {
                    color: 'rgba(255, 255, 255, 0.45)',
                  },
                },
                invalid: {
                  color: '#ff9a9a',
                  iconColor: '#ff9a9a',
                },
              },
              hidePostalCode: true
            }}
          />
        </S.CardElementWrapper>

        {/* The toast disappears; without this the member is left with a
            disabled button and no reason for it. */}
        {unsupportedBrand && (
          <S.UnsupportedBrandMessage>
            {UNSUPPORTED_CARD_BRAND_MESSAGE}
          </S.UnsupportedBrandMessage>
        )}

      </form>

      {/* CTA rendered into the slot outside the card. It stays in this React tree,
          so isCardValid/isLoading keep working; the `form` attribute restores the
          submit link. */}
      {renderCtaInSlot(
        <Button
          variant="brandPrimary"
          type="submit"
          form={SIGN_FORM_ID}
          disabled={!isDrawingValid || isLoading || !isCardValid || !clientSecret}
        >
          {isLoading ? (
            <>
              {loadingStatus} <S.ProcessingIcon size={18} />
            </>
          ) : (
            'Sign and Pay'
          )}
        </Button>,
        ctaSlot
      )}
    </>
  );
};
